/**
 * Whether the console has the screen to itself.
 *
 * Two ways in, and the console owes them the same answer: the operator pressed
 * fullscreen, or they installed it and the window *is* the app. Both are a
 * person saying "give the work the screen", and both are exactly where the
 * letterhead band stops earning its 30px — in an installed window the title bar
 * above it already reads "Kimene — yapay zekâ iş gücü kontrol düzlemi", which is
 * the band's own two strings, in order, one row higher.
 *
 * Read as state rather than written as a CSS media query, and that is
 * deliberate. `display-mode` cannot be emulated by Playwright and fullscreen is
 * awkward to drive there, so a pure-CSS rule would be a layout decision no test
 * in this repo could reach — the shape of defect that has cost this codebase
 * three days of a red viewport and a stylesheet nobody imported. As state, the
 * decision is a boolean a test can assert on.
 */

import { useEffect, useState } from 'react'

/** The queries that mean "this window is the application". `standalone` is the
 *  ordinary installed window; `window-controls-overlay` is the same window with
 *  the title bar handed to the page; `fullscreen` is the display mode a manifest
 *  can ask for, which is not the same thing as the Fullscreen API below. */
const APP_MODES = ['(display-mode: standalone)', '(display-mode: window-controls-overlay)', '(display-mode: fullscreen)']

/** Whether the document is running as an installed application.
 *
 *  `navigator.standalone` is the iOS Safari spelling and exists nowhere else;
 *  everything since reports it through `display-mode`. */
export function isStandaloneApp(): boolean {
  if (typeof window === 'undefined') return false
  const asIOS = window.navigator as Navigator & { standalone?: boolean }
  if (asIOS.standalone === true) return true
  if (typeof window.matchMedia !== 'function') return false
  return APP_MODES.some((q) => window.matchMedia(q).matches)
}

/** Whether the Fullscreen API currently holds an element, in either spelling. */
function inFullscreen(): boolean {
  if (typeof document === 'undefined') return false
  const doc = document as Document & { webkitFullscreenElement?: Element | null }
  return (document.fullscreenElement ?? doc.webkitFullscreenElement ?? null) !== null
}

export function useImmersive(): boolean {
  const [immersive, setImmersive] = useState(() => inFullscreen() || isStandaloneApp())

  useEffect(() => {
    const sync = () => setImmersive(inFullscreen() || isStandaloneApp())

    document.addEventListener('fullscreenchange', sync)
    document.addEventListener('webkitfullscreenchange', sync)

    // A window can move between display modes without a reload — dragging an
    // installed window out of fullscreen, or a browser that flips
    // window-controls-overlay off — so the queries are watched rather than read
    // once at mount.
    const lists =
      typeof window.matchMedia === 'function' ? APP_MODES.map((q) => window.matchMedia(q)) : []
    for (const list of lists) list.addEventListener?.('change', sync)

    return () => {
      document.removeEventListener('fullscreenchange', sync)
      document.removeEventListener('webkitfullscreenchange', sync)
      for (const list of lists) list.removeEventListener?.('change', sync)
    }
  }, [])

  return immersive
}
